import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ordersAPI } from '../services/api';
import './Payment.css';

const Payment = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(true);
  const [qrLoading, setQrLoading] = useState(true);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState({ text: '', type: '' });
  const [paymentData, setPaymentData] = useState(null);
  const [order, setOrder] = useState(null);
  const [orderCreatedAt, setOrderCreatedAt] = useState(new Date());
  const [timeAgo, setTimeAgo] = useState('Just now');
  const [confirmEnabled, setConfirmEnabled] = useState(false);
  const initialized = useRef(false);

  // Fetch payment details and QR code
  useEffect(() => {
    const initializePayment = async () => {
      if (initialized.current) return;
      initialized.current = true;

      try {
        setLoading(true);
        const response = await ordersAPI.initiatePayment(id);
        setPaymentData(response.data);
        setOrderCreatedAt(new Date(response.data.order?.created_at || new Date()));
        // Fetch order details if not included
        if (!response.data.order) {
          try {
            const orderRes = await ordersAPI.getById(id);
            setOrder(orderRes.data);
            setOrderCreatedAt(new Date(orderRes.data.created_at));
          } catch (err) {
            console.error('Failed to fetch order details:', err);
          }
        } else {
          setOrder(response.data.order);
        }
        setQrLoading(false);
        // Enable confirm button after 3 seconds
        setTimeout(() => {
          setConfirmEnabled(true);
        }, 3000);
      } catch (err) {
        console.error('Payment initialization error:', err);
        setError('Failed to load payment details. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    initializePayment();
  }, [id]);

  // Update timer
  useEffect(() => {
    const updateTimer = () => {
      const now = new Date();
      const diff = Math.floor((now - orderCreatedAt) / 1000); // seconds
      let timeString;
      if (diff < 60) {
        timeString = 'Just now';
      } else if (diff < 3600) {
        const minutes = Math.floor(diff / 60);
        timeString = `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      } else {
        const hours = Math.floor(diff / 3600);
        timeString = `${hours} hour${hours > 1 ? 's' : ''} ago`;
      }
      setTimeAgo(timeString);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, [orderCreatedAt]);

  // Confirm payment handler
  const handleConfirmPayment = async () => {
    setConfirmLoading(true);
    setStatusMessage({ text: '', type: '' });
    try {
      const response = await ordersAPI.confirmPayment(id);
      setStatusMessage({
        text: `✅ ${response.data.message}`,
        type: 'success'
      });
      // Redirect to orders page after 2 seconds
      setTimeout(() => {
        navigate('/orders');
      }, 2000);
    } catch (err) {
      console.error('Payment confirmation error:', err);
      const errorMsg = err.response?.data?.error || 'Failed to confirm payment';
      setStatusMessage({
        text: `❌ ${errorMsg}`,
        type: 'error'
      });
      setConfirmLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="payment-card">
            <div className="qr-section">
              <div className="qr-loading">
                <div className="spinner"></div>
                <p style={{ color: '#64748b', fontWeight: 600 }}>Loading payment details...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="payment-card">
            <div className="header">
              <h1>⚠️ Payment Error</h1>
              <p>{error}</p>
            </div>
            <button
              className="confirm-button"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { upi, amount, formatted_amount } = paymentData || {};
  const orderData = order || paymentData?.order || {};

  return (
    <div className="payment-page">
      <div className="payment-container">
        <div className="payment-card">
          <div className="header">
            <h1>💳 Complete Your Payment</h1>
            <p>Order #<span id="order-id">{orderData.id || id}</span></p>
          </div>

          <div className="order-info">
            <div className="order-info-row">
              <span className="order-info-label">Order ID</span>
              <span className="order-info-value">#{orderData.id || id}</span>
            </div>
            <div className="order-info-row">
              <span className="order-info-label">Items</span>
              <span className="order-info-value">
                {orderData.order_items?.reduce((sum, item) => sum + item.quantity, 0) || 0} items
              </span>
            </div>

            <div className="order-info-row">
              <span className="order-info-label">Item Total</span>
              <span className="order-info-value">
                ₹{(Number(orderData.items_total) || Math.max(0, Number(orderData.total_amount || 0) - Number(orderData.delivery_charge !== undefined ? orderData.delivery_charge : 50))).toFixed(2)}
              </span>
            </div>
            <div className="order-info-row">
              <span className="order-info-label">Delivery Charge</span>
              <span className="order-info-value" style={{ color: '#16a34a' }}>
                + ₹{Number(orderData.delivery_charge !== undefined ? orderData.delivery_charge : 50).toFixed(2)}
              </span>
            </div>
            <div className="order-info-row total-row">
              <span className="order-info-label">Total Amount</span>
              <span className="order-info-value amount">
                {orderData.formatted_total_amount || (formatted_amount || `₹${Number(amount || 0).toFixed(2)}`)}
              </span>
            </div>
          </div>

          <div className="instructions">
            <h3>How to Pay:</h3>
            <ol>
              <li>Open any <strong>UPI app</strong> (Google Pay, PhonePe, Paytm, etc.)</li>
              <li><strong>Scan the QR code</strong> below or enter the UPI ID manually</li>
              <li>Complete the payment in your UPI app</li>
              <li>Return here and click <strong>"I have made payment"</strong> button</li>
            </ol>
            <div style={{ marginTop: '16px', fontSize: '13px', color: '#64748b', lineHeight: '1.5' }}>
              <h3> Note: </h3> Please wait for your order payment confirmation from our admin. You can see your payment status in your order's status. your order processing will start after payment confirmation.
            </div>
          </div>

          <div className="qr-section">
            {qrLoading ? (
              <div className="qr-loading">
                <div className="spinner"></div>
                <p style={{ color: '#64748b', fontWeight: 600 }}>Generating QR Code...</p>
              </div>
            ) : (
              <div className="qr-code-container visible">
                <h3>📱 Scan to Pay with Any UPI App</h3>
                <img
                  src={upi?.qr_code}
                  alt="UPI Payment QR Code"
                  className="qr-code-image"
                />
                <div className="upi-vpa">
                  <div className="upi-vpa-label">UPI ID</div>
                  <div className="upi-vpa-value">{upi?.vpa || 'Loading...'}</div>
                </div>
              </div>
            )}
          </div>

          {/* Confirm Payment Button */}
          <button
            className={`confirm-button ${confirmLoading ? 'loading' : ''}`}
            onClick={handleConfirmPayment}
            disabled={!confirmEnabled || confirmLoading}
          >
            <span className="button-text">I have made the payment</span>
            <span className="button-spinner"></span>
          </button>

          <div style={{ marginTop: '12px', textAlign: 'center', fontSize: '13px', color: '#64748b' }}>
            For payment related query send email on: <a href="mailto:support@vihaanherbals.com" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: '500' }}>helps.vihaanherbals@gmail.com</a>
          </div>

          {/* Status Message */}
          {statusMessage.text && (
            <div className={`status-message visible ${statusMessage.type}`}>
              {statusMessage.text}
            </div>
          )}

          {/* Timer */}
          <div className="timer">
            Order placed: {timeAgo}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;